import { useState } from 'react'
import { formatQty, type InventoryPartDTO } from '@wf/shared'
import { useArchivedInventory, useCriticalParts, useInventory, useReactivatePart } from './useInventory'
import { CriticalSection } from './CriticalSection'
import { PartForm } from './PartForm'
import { StockMovePanel } from './StockMovePanel'

type Panel =
  | { kind: 'none' }
  | { kind: 'new' }
  | { kind: 'edit'; part: InventoryPartDTO }
  | { kind: 'withdraw' }
  | { kind: 'add' }

/**
 * Reiter „Inventar": Bestandsverwaltung (entkoppelt vom Einkauf-Board). Von oben nach unten: kritische Bestände,
 * Aktionen (Neues Teil / entnehmen / zuführen), Inventarliste mit Suche + Archiv-Ansicht.
 */
export function InventoryPage() {
  const list = useInventory()
  const critical = useCriticalParts()
  const reactivate = useReactivatePart()
  const [panel, setPanel] = useState<Panel>({ kind: 'none' })
  const [query, setQuery] = useState('')
  const [showArchive, setShowArchive] = useState(false)
  const archived = useArchivedInventory(showArchive)
  const close = () => setPanel({ kind: 'none' })

  const parts = list.data?.parts ?? []
  const q = query.trim().toLowerCase()
  const filtered = q
    ? parts.filter(
        (p) =>
          p.name.toLowerCase().includes(q) ||
          p.partNumber.toLowerCase().includes(q) ||
          (p.manufacturer ?? '').toLowerCase().includes(q),
      )
    : parts

  return (
    <section className="wf-view">
      <div>
        <h2 className="viewtitle">Inventar</h2>
        <p className="sub">Lagerbestand · Teile, kritische Bestände, Bewegungen</p>
      </div>

      {critical.data ? <CriticalSection parts={critical.data.parts} /> : null}

      <div className="wf-listfoot">
        <button type="button" className="wf-btn" onClick={() => setPanel({ kind: 'new' })}>
          Neues Teil
        </button>
        <button type="button" className="wf-btn wf-btn--ghost" onClick={() => setPanel({ kind: 'withdraw' })}>
          Teil entnehmen
        </button>
        <button type="button" className="wf-btn wf-btn--ghost" onClick={() => setPanel({ kind: 'add' })}>
          Teil zuführen
        </button>
      </div>

      {panel.kind === 'new' ? <PartForm onClose={close} /> : null}
      {panel.kind === 'edit' ? <PartForm part={panel.part} onClose={close} /> : null}
      {panel.kind === 'withdraw' ? <StockMovePanel mode="withdraw" parts={parts} onClose={close} /> : null}
      {panel.kind === 'add' ? <StockMovePanel mode="add" parts={parts} onClose={close} /> : null}

      <input
        className="wf-input"
        placeholder="Suche (Bezeichnung, SNR, Hersteller)"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        aria-label="Inventar durchsuchen"
      />

      {list.isLoading ? (
        <div className="muted">Inventar wird geladen…</div>
      ) : filtered.length === 0 ? (
        <div className="wf-empty">
          <div className="wf-empty__title">{parts.length === 0 ? 'Noch keine Teile' : 'Kein Treffer'}</div>
        </div>
      ) : (
        <ul className="wf-linelist">
          {filtered.map((p) => {
            const low = p.stockTenths < p.safetyStockTenths
            return (
              <li key={p.id} className="wf-line">
                <span className="wf-line__main">
                  <span className="wf-line__name">{p.name}</span>
                  <span className="wf-line__sub wf-tnum">{p.partNumber}</span>
                </span>
                <span className={low ? 'wf-line__status error' : 'wf-line__status'}>
                  {formatQty(p.stockTenths)} {p.unit}
                  {low ? ' ⚠' : ''}
                </span>
                <button type="button" className="wf-btn wf-btn--ghost wf-btn--sm" onClick={() => setPanel({ kind: 'edit', part: p })}>
                  Bearbeiten
                </button>
              </li>
            )
          })}
        </ul>
      )}

      <button type="button" className="wf-btn wf-btn--ghost wf-btn--sm" onClick={() => setShowArchive((s) => !s)}>
        {showArchive ? 'Archiv ausblenden' : 'Archiv anzeigen'}
      </button>
      {showArchive ? (
        <div>
          <h3 className="wf-sectionhead">Archiv</h3>
          {archived.isLoading ? (
            <div className="muted">…</div>
          ) : (archived.data?.parts ?? []).length === 0 ? (
            <div className="muted">Keine archivierten Teile.</div>
          ) : (
            <ul className="wf-linelist">
              {(archived.data?.parts ?? []).map((p) => (
                <li key={p.id} className="wf-line">
                  <span className="wf-line__main">
                    <span className="wf-line__name">{p.name}</span>
                    <span className="wf-line__sub wf-tnum">{p.partNumber}</span>
                  </span>
                  <button
                    type="button"
                    className="wf-btn wf-btn--ghost wf-btn--sm"
                    disabled={reactivate.isPending}
                    onClick={() => reactivate.mutate({ id: p.id, body: {} })}
                  >
                    Reaktivieren
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : null}
    </section>
  )
}
